import React, { useState } from "react";
import axios from "axios";
import {
  Search,
  Filter,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2
} from "lucide-react";
import { exportResultsToCSV } from "./HelperFunctions/CsvDownloaderFunction";

const apiUrl = process.env.REACT_APP_API_URL;

const QueryEngine = ({ region, accountName, duration, accessToken,handleEventClick}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [queryResults, setQueryResults] = useState([]);
  const [isQuerying, setIsQuerying] = useState(false);
  const [queryFilters, setQueryFilters] = useState({
    eventName: '',
    eventSource: '',
    username: '',
    accessKeyId: '',
    eventId: ''
  });
  const handleFilterChange = (field, value) => {
    setQueryFilters({
      ...queryFilters,
      [field]: value
    });
  };

  const runQuery = async () => {
    const query = `
      query GetQueryEngineData($duration: String!, $accountName: String!, $region: String!, $eventName: String, $username: String, $eventSource: String, $accessKeyId: String, $eventId: String, $resourceName: String, $resourceType: String) {
        getQueryEngineData(
          duration: $duration
          filters: {
            accountName: $accountName,
            region: $region,
            queryEngineFilters: {
              eventName: $eventName,
              username: $username,
              eventSource: $eventSource,
              accessKeyId: $accessKeyId,
              eventId: $eventId,
              resourceName: $resourceName,
              resourceType: $resourceType
            }
          }
        ) {
          data {
            awsRegion
            errorCode
            errorMessage
            eventName
            eventSource
            eventType
            eventTime
            resourceType
            responseElements
            sourceIpAddress
            username
            resourceName
            requestParameters
          }
        }
      }
    `;

    const variables = {
      duration: duration,
      accountName: accountName,
      region: region,
      eventName: queryFilters.eventName,
      username: queryFilters.username,
      eventSource: queryFilters.eventSource,
      accessKeyId: queryFilters.accessKeyId,
      eventId: queryFilters.eventId,
      resourceName: queryFilters.resourceName,
      resourceType: queryFilters.resourceType
    };

    try {
      setCurrentPage(1);
      setIsQuerying(true);
      setQueryResults([]);
      const response = await axios.post(
        apiUrl, 
        { query, variables },
        {
          headers: {
            Authorization: accessToken, 
          },
        }
      );
      
      if (response.data && response.data.data) {
        const results = response.data.data.getQueryEngineData.data;
        setQueryResults(results);
      }
    } catch (error) {
      console.error("Error executing query:", error);
    } finally {
      setIsQuerying(false);
    }
  };

  const resetFilters = () => {
    setCurrentPage(1);
    setQueryFilters({
      eventName: '',
      eventSource: '',
      username: '',
      accessKeyId: '',
      eventId: ''
    });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
     <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Search className="h-5 w-5 text-[#003366]" />
                <h2 className="text-xl font-semibold text-gray-800">Event Query Engine</h2>
              </div>
              <div className="flex items-center gap-3">
              <button 
    className={`flex items-center gap-1 px-3 py-1.5 bg-[#003366] text-white rounded-md hover:bg-[#002244] transition-colors ${
      isQuerying ? "opacity-70 cursor-not-allowed" : ""
    }`}
    onClick={runQuery}
    disabled={isQuerying}
  >
    {isQuerying ? ( 
      <>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Querying...</span>
      </>
    ) : (
      <>
        <Search className="h-4 w-4" />
        <span>Run Query</span>
      </>
    )}
  </button>
                <button 
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  onClick={resetFilters}
                >
                  <Filter className="h-4 w-4" />
                  <span>Reset Filters</span>
                </button>
              </div>
            </div>
            
            {/* Filter Section */}
            <div className="grid grid-cols-3 gap-4 mb-6">
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Event Name</label>
      <input
        type="text"
        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        placeholder="e.g. RunInstances, CreateBucket"
        value={queryFilters.eventName}
        onChange={(e) => handleFilterChange('eventName', e.target.value)}
      />
    </div>
    
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Event Source</label>
      <input
        type="text"
        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        placeholder="e.g. ec2.amazonaws.com"
        value={queryFilters.eventSource}
        onChange={(e) => handleFilterChange('eventSource', e.target.value)}
      />
    </div>
    
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Username</label>
      <input
        type="text"
        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter username"
        value={queryFilters.username}
        onChange={(e) => handleFilterChange('username', e.target.value)}
      />
    </div>
  
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Access Key ID</label>
      <input
        type="text"
        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter access key ID"
        value={queryFilters.accessKeyId}
        onChange={(e) => handleFilterChange('accessKeyId', e.target.value)}
      />
    </div>
  
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Event ID</label>
      <input
        type="text"
        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter event ID"
        value={queryFilters.eventId}
        onChange={(e) => handleFilterChange('eventId', e.target.value)}
      />
    </div>
  </div>
            
            {/* Results Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
    <div className="flex justify-between items-center p-4 bg-gray-50 border-b border-gray-200">
      <h3 className="font-medium text-gray-700">
        Event Results ({queryResults?.length || 0})
      </h3>
      <button 
        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        onClick={() => exportResultsToCSV(queryResults)}
        disabled={isQuerying || queryResults?.length === 0} 
      >
        <Download className="h-4 w-4" />
        <span>Export Results</span>
      </button>
    </div>
    
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Time
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Event Name
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Source
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Resource ID
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              IP Address
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {isQuerying ? (
            Array(2).fill().map((_, index) => (
              <tr key={`skeleton-${index}`} className="animate-pulse">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="h-4 bg-gray-200 rounded w-40"></div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="h-4 bg-gray-200 rounded w-28"></div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="h-4 bg-gray-200 rounded w-36"></div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </td>
              </tr>
            ))
          ) : queryResults?.length > 0 ? (
            queryResults
              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
              .map((event) => (
              <tr key={event.id} className="hover:bg-gray-50 cursor-pointer">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                    {formatTimestamp(event.eventTime)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" onClick={() => handleEventClick(event)}>
                  {event.eventName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {event.eventSource}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {event.username}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {event.resourceName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {event.sourceIpAddress}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="px-6 py-10 text-center text-sm text-gray-500">
                No events found matching your search criteria.<br />
                Please note that the eventnames are case sensitive.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    {queryResults?.length > 0 && (
      <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center text-sm text-gray-500">
          <span>Showing {Math.min(queryResults.length, (currentPage - 1) * itemsPerPage + 1)} to {Math.min(queryResults.length, currentPage * itemsPerPage)} of {queryResults.length} results</span>
          <div className="ml-4">
            <select 
              className="border border-gray-300 rounded p-1 text-sm"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
        {/* First page button */}
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className={`p-2 rounded ${
            currentPage === 1
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-200'
          }`}
          aria-label="First page"
        >
          <span className="flex">
            <ChevronLeft className="h-4 w-4" />
            <ChevronLeft className="h-4 w-4 -ml-2" />
          </span>
        </button>
        
        {/* Previous page button */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 rounded ${
            currentPage === 1
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-200'
          }`}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        
        {/* Page Numbers */}
        <div className="flex items-center mx-2">
          {Array.from({ length: Math.min(5, Math.ceil(queryResults.length / itemsPerPage)) }, (_, i) => {
            const totalPages = Math.ceil(queryResults.length / itemsPerPage);
            let pageNum;
            
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`w-8 h-8 flex items-center justify-center rounded ${
                  currentPage === pageNum
                    ? 'bg-[#003366] text-white'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
                aria-label={`Page ${pageNum}`}
                aria-current={currentPage === pageNum ? "page" : undefined}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === Math.ceil(queryResults.length / itemsPerPage)}
          className={`p-2 rounded ${
            currentPage === Math.ceil(queryResults.length / itemsPerPage)
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-200'
          }`}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          onClick={() => handlePageChange(Math.ceil(queryResults.length / itemsPerPage))}
          disabled={currentPage === Math.ceil(queryResults.length / itemsPerPage)}
          className={`p-2 rounded ${
            currentPage === Math.ceil(queryResults.length / itemsPerPage)
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-200'
          }`}
          aria-label="Last page"
        >
          <span className="flex">
            <ChevronRight className="h-4 w-4" />
            <ChevronRight className="h-4 w-4 -ml-2" />
          </span>
        </button>
      </div>
      </div>
    )}
  </div>
          </div>
  );
};

export default QueryEngine;